import axios, { AxiosInstance } from 'axios';
import firebase from 'firebase/app';
import * as demo from './krAutomationDemoBackend';

// Lets you point only the KR-automation endpoints at a local Functions emulator
// without re-routing the rest of the app. Falls back to the global API URL.
const baseURL =
  process.env.VUE_APP_KR_AUTOMATION_API_URL ||
  process.env.VUE_APP_API_URL ||
  '';

export const isKrAutomationDemoMode = (): boolean => demo.isDemoMode();

let client: AxiosInstance | null = null;

const getClient = (): AxiosInstance => {
  if (client) return client;
  client = axios.create({ baseURL, timeout: 65000 });
  client.interceptors.request.use(async (config) => {
    const user = firebase.auth().currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers = config.headers || {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
  });
  return client;
};

/**
 * One sub-recipe per owner, for individual-mode KRs. The cron fans out
 * these calls in parallel and routes each result into owner_progress[email].
 * Same shape across all providers (Happily + Composio).
 */
export interface SubRecipe {
  owner_email: string;
  owner_label?: string;
  action_args: Record<string, unknown>;
  resolution_status?: 'matched' | 'unmatched';
  resolution_note?: string;
}

export interface CompiledRecipe {
  action_slug: string;
  /** Single-call args. Required when sub_recipes is absent (collaborative mode). */
  action_args?: Record<string, unknown>;
  /** Per-owner fan-out. Present in individual-mode recipes. Mutually exclusive with action_args at runtime. */
  sub_recipes?: SubRecipe[];
  /** Owners we couldn't match in the provider's directory. Surfaced in the dialog. */
  unmatched_owners?: string[];
  /**
   * Date-window spec. `rolling` windows are recomputed from "now" by the
   * runners before each sync; `fixed` windows (a quarter, a month) run as
   * saved. Absent on recipes compiled before this shipped.
   */
  window?: {
    type: 'rolling' | 'fixed';
    days?: number;
    start?: string;
    end?: string;
    date_property?: string;
  };
  post_processing: {
    result_path: string;
    aggregation: 'sum' | 'count' | 'avg' | 'min' | 'max';
    value_field?: string;
    date_field?: string;
    /** With date_field set, the backend drops records outside this range before aggregating. */
    date_range?: { start?: string; end?: string };
    /** Multiplier applied to the final aggregate; 0.01 for minor-unit money (Stripe cents). */
    value_scale?: number;
    currency?: string;
  };
}

export interface ComposeRecipeResponse {
  recipe: CompiledRecipe;
  human_summary: string;
  suggested_toolkit_slug: string;
  suggested_toolkit_name: string;
  needs_clarification: string[];
}

export interface PreviewResponse {
  value: number;
  sample_note: string;
  source_summary: {
    records_counted: number;
    date_range?: { start: string; end: string };
    filters_applied: string[];
    sample_records?: Array<{ label: string; value: string }>;
  };
  /** Present for per-owner (individual-mode) recipes. Same shape across all providers. */
  owner_breakdown?: Array<{ email: string; label: string; value: number }> | null;
  /** Owners the provider couldn't match to its directory. Surfaced as warnings too. */
  unmatched_owners?: string[];
  warnings: string[];
}

export interface ConnectedAccount {
  id: string;
  toolkit_slug: string;
  display_label: string;
  status: 'active' | 'expired' | 'revoked' | 'error';
  connected_by_email: string;
  created_at: string;
}

export interface Toolkit {
  slug: string;
  name: string;
  logo_url?: string;
  has_query_actions: boolean;
  /**
   * Composio's category metadata, when available. Each toolkit can belong to
   * multiple categories. Used as a fallback label for toolkits that aren't in
   * the curated tile list.
   */
  categories?: Array<{ slug: string; name: string }>;
}

export interface CadenceConfig {
  frequency: 'weekly' | 'daily' | 'monthly';
  day?: string;
  day_of_month?: number;
  time: string;
  suppress_if_no_meaningful_change: boolean;
}

export interface NotificationPolicy {
  enabled: boolean;
  notify_on: 'published_update';
  recipients: {
    kr_owners: boolean;
    goal_owner: boolean;
    specific_user_ids: string[];
    manager_chain: false;
  };
}

export interface MetricSourcePayload {
  enabled: boolean;
  toolkit_slug: string;
  connected_account_id: string;
  original_prompt: string;
  compiled_recipe: CompiledRecipe;
  cadence: CadenceConfig;
  notification_policy: NotificationPolicy;
}

const post = async <T>(path: string, body: unknown): Promise<T> => {
  const res = await getClient().post<T>(path, body);
  return res.data;
};

const get = async <T>(path: string, params?: Record<string, unknown>): Promise<T> => {
  const res = await getClient().get<T>(path, { params });
  return res.data;
};

/**
 * KR context passed to compose / preview. The auto-fill fields (owner_email,
 * owner_emails, team_id, mode) let Happily-native metric specs resolve
 * identifier args server-side without requiring the user to type them.
 *
 * mode === 'individual' switches the resolution from one owner_email to the
 * full owner_emails array; the runner returns one record per owner so the
 * cron can update each owner_progress slot independently.
 *
 * Composio recipes ignore all of these except title/unit/target.
 */
export interface KrContext {
  title: string;
  unit?: string;
  target_value?: number;
  start_value?: number;
  current_value?: number;
  owner_email?: string;
  owner_emails?: string[];
  team_id?: string;
  company_id?: string;
  mode?: 'collaborative' | 'individual' | string;
}

export const composeMetricRecipe = (params: {
  prompt: string;
  kr_context: KrContext;
  toolkit_hint?: string;
}): Promise<ComposeRecipeResponse> =>
  demo.isDemoMode() ? demo.demoComposeMetricRecipe(params) : post('/composeMetricRecipe', params);

export const previewMetricRecipe = (params: {
  recipe: CompiledRecipe;
  /** Required for Composio recipes. Omit for Happily-native recipes (toolkit_slug='happily'). */
  connected_account_id?: string;
  /** Set to 'happily' for Happily-native recipes. Omitted for Composio. */
  toolkit_slug?: string;
  kr_context: KrContext;
  regenerate_note?: boolean;
}): Promise<PreviewResponse> =>
  demo.isDemoMode() ? demo.demoPreviewMetricRecipe(params) : post('/previewMetricRecipe', params);

export const saveKrMetricSource = (params: {
  company_id: string;
  goal_id: string;
  kr_id: string;
  metric_source: MetricSourcePayload;
}): Promise<{
  ok: boolean;
  next_run_at: string;
  first_sync?: {
    status: 'success' | 'no_change' | 'zero_records' | 'auth_expired' | 'error';
    new_value?: number;
    records_counted?: number;
    error?: string;
  } | null;
}> =>
  demo.isDemoMode() ? demo.demoSaveKrMetricSource(params) : post('/saveKrMetricSource', params);

export const runKrNow = (params: {
  company_id: string;
  goal_id: string;
  kr_id: string;
}): Promise<{ accepted: boolean }> =>
  demo.isDemoMode() ? demo.demoRunKrNow(params) : post('/runKrNow', params);

export const listConnectedAccounts = (params?: {
  toolkit_slug?: string;
}): Promise<{ accounts: ConnectedAccount[] }> =>
  demo.isDemoMode() ? demo.demoListConnectedAccounts(params) : get('/listConnectedAccounts', params);

export const startConnectAccount = (params: {
  toolkit_slug: string;
}): Promise<{ redirect_url: string; state_token: string }> =>
  demo.isDemoMode() ? demo.demoStartConnectAccount(params) : post('/startConnectAccount', params);

export const completeDemoConnection = (toolkitSlug: string) =>
  demo.demoCompleteConnection(toolkitSlug);

export const revokeConnectedAccount = (params: {
  account_id: string;
}): Promise<{ ok: boolean }> =>
  demo.isDemoMode() ? demo.demoRevokeConnectedAccount(params) : post('/revokeConnectedAccount', params);

export const listToolkits = (params?: {
  q?: string;
}): Promise<{ toolkits: Toolkit[] }> =>
  demo.isDemoMode() ? demo.demoListToolkits(params) : get('/listToolkits', params);

export const getKrAutomationHealth = (params: {
  goal_id: string;
  kr_id: string;
}): Promise<{
  last_synced_at?: string;
  last_error?: { code: string; message: string; at: string };
  recent_runs: Array<{ id: string; status: string; started_at: string; new_value?: number }>;
}> => get('/getKrAutomationHealth', params);

export type SyncRunStatus = demo.SyncRunStatus;
export type SyncRun = demo.SyncRun;

export const listSyncRuns = (params?: {
  toolkit_slug?: string;
  status?: SyncRunStatus | 'all' | 'failures';
  limit?: number;
}): Promise<{ runs: SyncRun[]; failure_count_24h: number }> =>
  demo.isDemoMode() ? demo.demoListSyncRuns(params) : get('/listSyncRuns', params);
